// Model-Lock v2/v4 (Spec: docs/superpowers/specs/2026-07-23-model-lock-design.md;
// seit 2026-09-29 v4 statt v3, siehe docs/decisions.md).
//
// Admin kann das erlaubte ElevenLabs-TTS-Model pro Voice und pro Asset
// einschränken (`Voice.model` / `Asset.model`; nicht gesetzt = alle).
// Im Formular sind das Checkboxen je Model (ModelCheckboxes): alle
// angehakt = null, genau eines = dieses Model.
// Auflösung: die Voice-Einschränkung gewinnt vor der Asset-Einschränkung —
// „wenn pro Voice begrenzt wird, ist es pro Asset auch festgelegt".
// Konflikte (Voice v2-only × Asset v4-only) verhindert die Save-Validierung
// im Admin-Formular via findModelConflicts. Custom-Assets und rohe
// customVoiceIds bleiben unbeschränkt (kein Voice-Objekt → kein Lock).

import type { TtsModel } from '@shared/types';

/** Alle wählbaren TTS-Models in Anzeigereihenfolge. */
export const TTS_MODELS: readonly TtsModel[] = ['v2', 'v4'];

/** Anzeigename je Model (Talent-Panel, Admin-Checkboxen, Params.txt). */
export const TTS_MODEL_LABELS: Record<TtsModel, string> = {
  v2: 'Multilingual v2',
  v4: 'Eleven v4',
};

/** ElevenLabs-`model_id` je Model. */
export const TTS_MODEL_IDS: Record<TtsModel, string> = {
  v2: 'eleven_multilingual_v2',
  v4: 'eleven_v4',
};

/** Gespeicherte Model-Werte vereinheitlichen: `'v3'` (seit 2026-09-29 aus
 *  dem Tool entfernt) wird zu `'v4'`, Unbekanntes zu null. Gegenstück im
 *  Dienst: `normalizeModelLock` in `server/kv.ts`. */
export function normalizeTtsModel(value: unknown): TtsModel | null {
  if (value === 'v2' || value === 'v4') return value;
  if (value === 'v3') return 'v4';
  return null;
}

/** Effektiver Model-Lock für einen Block: `voice.model ?? asset.model ??
 *  null` (null = Talent darf frei wählen). Unbekannte voiceIds (Custom-
 *  Voices) haben keine Voice-Einschränkung. */
export function resolveModelLock(
  asset: { model?: TtsModel | null } | undefined,
  voiceId: string | undefined,
  voices: ReadonlyArray<{ id: string; model?: TtsModel | null }>,
): TtsModel | null {
  const voice = voiceId ? voices.find((v) => v.id === voiceId) : undefined;
  return voice?.model ?? asset?.model ?? null;
}

export interface ModelConflict {
  assetTitle: string;
  voiceName: string;
}

/** Save-Validierung: jede TTS-fähige Asset×Voice-Kombination muss mindestens
 *  ein gemeinsames Model haben. Konflikt = beide Seiten gesetzt und ungleich.
 *  STS-Assets sind ausgenommen (Model betrifft nur TTS). */
export function findModelConflicts(
  assets: ReadonlyArray<{
    title: string;
    voiceIds?: string[];
    model?: TtsModel | null;
    assetType?: string;
  }>,
  voices: ReadonlyArray<{ id: string; name: string; model?: TtsModel | null }>,
): ModelConflict[] {
  const byId = new Map(voices.map((v) => [v.id, v] as const));
  const out: ModelConflict[] = [];
  for (const asset of assets) {
    if (asset.assetType === 'sts') continue;
    if (!asset.model) continue; // asset erlaubt beide → nie ein Konflikt
    for (const vid of asset.voiceIds ?? []) {
      const voice = byId.get(vid);
      if (!voice?.model) continue; // unbekannte Voice oder beide erlaubt
      if (voice.model !== asset.model) {
        out.push({ assetTitle: asset.title, voiceName: voice.name });
      }
    }
  }
  return out;
}

/** Ist `model` unter dem Lock `value` erlaubt? (null = alle). */
export function isModelAllowed(value: TtsModel | null | undefined, model: TtsModel): boolean {
  return value == null || value === model;
}

/** Checkbox-Logik des Admin-Formulars (ModelCheckboxes): Model `model` an-
 *  bzw. abhaken und den neuen Lock-Wert liefern. Alle angehakt = null,
 *  genau eines = dieses Model. Das letzte angehakte Model lässt sich nicht
 *  abhaken (mindestens eines bleibt erlaubt) — dann kommt `value` unverändert
 *  zurück. Mit zwei Models ist jede erlaubte Teilmenge als Einzelwert
 *  darstellbar; ein drittes Model bräuchte hier ein Array. */
export function toggleModel(
  value: TtsModel | null | undefined,
  model: TtsModel,
  checked: boolean,
): TtsModel | null {
  const allowed = new Set(TTS_MODELS.filter((m) => isModelAllowed(value, m)));
  if (checked) allowed.add(model);
  else allowed.delete(model);
  if (allowed.size === 0) return value ?? null;
  if (allowed.size === TTS_MODELS.length) return null;
  return [...allowed][0];
}
